import { getConditionOperatorEvaluationFunc } from '@unifyapps/carbon/no-code/hooks/useComputeConditionEvaluationGetter';
import { getNoCodeBulkComputedData } from '@unifyapps/carbon/no-code/utils/getNoCodeComputeData';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type {
  ThemeConditionalFilter,
  ThemeConditionalValueFilter,
} from '@unifyapps/defs/types/interface';
import _isEmpty from 'lodash/isEmpty';

/**
 * Type guard to check if a value is a ThemeConditionalValueFilter
 */
export function isThemeConditionalValueFilter(
  value: string | ThemeConditionalValueFilter | undefined,
): value is ThemeConditionalValueFilter {
  return (
    typeof value === 'object' &&
    value !== null &&
    'conditionalValueFilters' in value &&
    'defaultValue' in value
  );
}

/**
 * Extract the default theme ID from a defaultThemeId value
 * Returns the string directly if it's a string, or the defaultValue from the filter
 */
export function getDefaultThemeIdValue(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
): string | undefined {
  if (!defaultThemeId) {
    return undefined;
  }
  if (isThemeConditionalValueFilter(defaultThemeId)) {
    return defaultThemeId.defaultValue;
  }
  return defaultThemeId;
}

/**
 * Extract the conditional theme filters from a defaultThemeId value
 * Returns empty array if it's a simple string
 */
export function getConditionalThemeFilters(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
): ThemeConditionalFilter[] {
  if (!defaultThemeId || !isThemeConditionalValueFilter(defaultThemeId)) {
    return [];
  }
  return defaultThemeId.conditionalValueFilters;
}

/**
 * Check if conditional themes exist
 */
export function hasConditionalThemes(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
): boolean {
  if (!defaultThemeId || !isThemeConditionalValueFilter(defaultThemeId)) {
    return false;
  }
  return defaultThemeId.conditionalValueFilters.length > 0;
}

export function getEvaluatedThemeId(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
  context: Record<string, unknown>,
  shouldEvaluateResult?: (result: string) => boolean,
): string | undefined {
  if (!defaultThemeId) {
    return undefined;
  }
  const conditionalFilters = getConditionalThemeFilters(defaultThemeId);
  const defaultThemeIdValue = getDefaultThemeIdValue(defaultThemeId);
  for (const filter of conditionalFilters) {
    const isConditionMet = getConditionOperatorEvaluationFunc({
      conditionEvaluationObj: filter.conditions,
      context,
      getNoCodeBulkComputedData: <T extends Record<string, string>>(
        data: T,
        ctx: Record<string, unknown>,
      ) => getNoCodeBulkComputedData<T>(data, ctx, shouldEvaluateResult),
      isPreComputed: false,
    });

    if (isConditionMet === true) {
      return filter.propertyValue;
    }
  }
  return defaultThemeIdValue;
}

/**
 * Create a new ThemeConditionalValueFilter
 */
export function createThemeConditionalValueFilter(
  defaultValue: string,
  filters: ThemeConditionalFilter[] = [],
): ThemeConditionalValueFilter {
  return {
    conditionalValueFilters: filters,
    defaultValue,
  };
}

/**
 * Add a theme to conditional filters
 * If the current value is a string, converts to ThemeConditionalValueFilter
 */
export function addThemeToConditional(
  current: string | ThemeConditionalValueFilter | undefined,
  themeId: string,
  condition?: ConditionEvaluationObject,
): ThemeConditionalValueFilter {
  const defaultValue = getDefaultThemeIdValue(current);
  const existingFilters = getConditionalThemeFilters(current);

  // Check if theme already exists in filters
  const themeExists = existingFilters.some((filter) => filter.propertyValue === themeId);
  if (themeExists) {
    // Return as is if theme already exists
    if (isThemeConditionalValueFilter(current)) {
      return current;
    }
    return createThemeConditionalValueFilter(defaultValue ?? '', existingFilters);
  }

  const newFilter: ThemeConditionalFilter = {
    propertyValue: themeId,
    conditions: condition,
  };

  return createThemeConditionalValueFilter(defaultValue ?? '', [...existingFilters, newFilter]);
}

/**
 * Remove a theme from conditional filters
 * If the removed theme was the default, and there are remaining themes, the first conditional becomes default
 * Returns undefined if no themes remain, string if only default remains, or ThemeConditionalValueFilter otherwise
 */
export function removeThemeFromConditional(
  current: string | ThemeConditionalValueFilter | undefined,
  themeId: string,
): string | ThemeConditionalValueFilter | undefined {
  // If it's a simple string, just check if it matches
  if (!current) {
    return undefined;
  }

  if (!isThemeConditionalValueFilter(current)) {
    // It's a string - if it matches themeId, return undefined
    return current === themeId ? undefined : current;
  }

  const defaultValue = current.defaultValue;
  const filters = current.conditionalValueFilters.filter(
    (filter) => filter.propertyValue !== themeId,
  );

  // If the deleted theme was the default
  if (defaultValue === themeId) {
    // If there are remaining conditional themes, make the first one the default
    if (filters.length > 0) {
      const [newDefault, ...remainingFilters] = filters;
      if (remainingFilters.length === 0) {
        // Only one theme left, return as string
        return newDefault.propertyValue;
      }
      return createThemeConditionalValueFilter(newDefault.propertyValue, remainingFilters);
    }
    // No themes left
    return undefined;
  }

  // Default was not deleted
  if (filters.length === 0) {
    // No conditional themes left, return just the default as string
    return defaultValue;
  }

  return createThemeConditionalValueFilter(defaultValue, filters);
}

/**
 * Set a theme as the default
 * The current default moves to conditional filters, the new theme becomes default
 */
export function setThemeAsDefault(
  current: string | ThemeConditionalValueFilter | undefined,
  newDefaultThemeId: string,
): string | ThemeConditionalValueFilter {
  // If no current value, just set the new default
  if (!current) {
    return newDefaultThemeId;
  }

  // If it's a simple string
  if (!isThemeConditionalValueFilter(current)) {
    if (current === newDefaultThemeId) {
      // Already the default
      return current;
    }
    // Move current default to conditional, set new default
    return createThemeConditionalValueFilter(newDefaultThemeId, [
      { propertyValue: current, conditions: undefined },
    ]);
  }

  const oldDefaultValue = current.defaultValue;
  let filters = current.conditionalValueFilters;

  // If the new default is already the default, return as is
  if (oldDefaultValue === newDefaultThemeId) {
    return current;
  }

  // Find and remove the new default from conditional filters (if it exists there)
  const newDefaultFilter = filters.find((f) => f.propertyValue === newDefaultThemeId);
  filters = filters.filter((f) => f.propertyValue !== newDefaultThemeId);

  // Add old default to conditional filters (preserve no condition, or use existing if it had one)
  filters = [
    { propertyValue: oldDefaultValue, conditions: newDefaultFilter?.conditions },
    ...filters,
  ];

  return createThemeConditionalValueFilter(newDefaultThemeId, filters);
}

/**
 * Update the condition for a theme in conditional filters
 */
export function updateThemeCondition(
  current: string | ThemeConditionalValueFilter | undefined,
  themeId: string,
  condition: ConditionEvaluationObject | undefined,
): string | ThemeConditionalValueFilter | undefined {
  if (!current || !isThemeConditionalValueFilter(current)) {
    // Can't update condition on a string
    return current;
  }

  const filters = current.conditionalValueFilters.map((filter) => {
    if (filter.propertyValue === themeId) {
      return { ...filter, conditions: condition };
    }
    return filter;
  });

  return createThemeConditionalValueFilter(current.defaultValue, filters);
}

/**
 * Get all theme IDs (default + conditional)
 */
export function getAllThemeIds(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
): string[] {
  const defaultId = getDefaultThemeIdValue(defaultThemeId);
  const conditionalIds = getConditionalThemeFilters(defaultThemeId).map((f) => f.propertyValue);

  const allIds: string[] = [];
  if (defaultId) {
    allIds.push(defaultId);
  }
  for (const id of conditionalIds) {
    if (!_isEmpty(id) && !allIds.includes(id)) {
      allIds.push(id);
    }
  }

  return allIds;
}

/**
 * Find a conditional filter by theme ID
 */
export function findConditionalFilter(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
  themeId: string,
): ThemeConditionalFilter | undefined {
  const filters = getConditionalThemeFilters(defaultThemeId);
  return filters.find((f) => f.propertyValue === themeId);
}

/**
 * Check if a theme is the default theme
 */
export function isDefaultTheme(
  defaultThemeId: string | ThemeConditionalValueFilter | undefined,
  themeId: string,
): boolean {
  const defaultId = getDefaultThemeIdValue(defaultThemeId);
  return defaultId === themeId;
}
